import { HISTORICAL_NUMBER_OF_DAYS_FOR_REGIONAL_ONLY_USERS } from '@semios/app-platform-values-helpers-and-types'
import moment from 'moment-timezone'
import { PersistentStoreName } from 'stores/_utils/persistentStore/persistenceStoresCleanUpMap'
import { persistentInit } from 'stores/_utils/persistentStore/persistentStore'
import { layersNavigationMenuStore } from 'stores/layersNavigationMenuStore/layersNavigationMenuStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { getIsoWithoutTheZ } from 'utils/getIsoWithoutTheZ'
import { getScreenSize } from 'utils/getScreenSize'

type ISOStringWithoutTheZed = string

export type TPrecipitationAggregation = 'HOURLY' | 'DAILY'

export type TEvapotranspirationValueToShow = 'ETC' | 'ETO'

export type TChillMarVsMay = 'MAR' | 'MAY'

export type TChillSepVsNov = 'SEP' | 'NOV'

export type TSelectedTab = 'OVERVIEW' | 'GRAPHS' | 'TABLES' | 'STATUS' | ''

export type TSoilIrrigationActivityScope = 'PROBE' | 'ZONE'

export type TPanelPlacement = 'bottom' | 'right'

export type TDraggedPanelSize = { placement: TPanelPlacement; size: number }

export const detailsPanelStore = persistentInit<{
  allSoilDepthsAreSelected: boolean
  showSelectedSoilDepths: boolean
  chillMarVsMay: TChillMarVsMay
  chillSepVsNov: TChillSepVsNov
  compareSeasonsInterval: number
  dateFrom: ISOStringWithoutTheZed
  dateTo: ISOStringWithoutTheZed
  wmDateFrom: ISOStringWithoutTheZed
  wmDateTo: ISOStringWithoutTheZed
  selectedTab: TSelectedTab
  evapotranspirationValueToShow: TEvapotranspirationValueToShow
  keyForRefreshing: string | null
  keyForRefreshingSummaryTab: string | null
  keyForRedrawing: string | null
  larvalTrendShowValues: boolean
  precipitationAggregation: TPrecipitationAggregation
  soilIrrigationActivityScope: TSoilIrrigationActivityScope | null
  soilVisibility: Record<string, boolean>
  showDateRangeDoesntApply: boolean
  soilSettingsOpened: boolean
  soilCalibrationSettingsOpened: boolean
  refillPointDatesSettingsOpened: boolean
  kcBlockSettingsModalOpened: boolean
  fruitGrowthSettingsOpened: boolean
  plantStressSettingsOpened: boolean
  alternariaSettingsOpened: boolean
  almondEventSettingsOpened: boolean
  almondHullSplitSettingOpened: boolean
  fireBlightSettingsOpened: boolean
  biofixModalOpenedForInsect: { insectId: number; propertyId: number } | null
  flowMeterLimitsSettingsOpened: boolean
  isPanelOpen: boolean
  draggedPanelSize: TDraggedPanelSize | null
}>({
  initialState: {
    allSoilDepthsAreSelected: false,
    showSelectedSoilDepths: false,
    chillMarVsMay: 'MAR',
    chillSepVsNov: 'SEP',
    compareSeasonsInterval: 0,
    dateFrom: getIsoWithoutTheZ(moment.tz(moment.tz.guess()).subtract(3, 'days').startOf('day')),
    dateTo: getIsoWithoutTheZ(moment.tz(moment.tz.guess()).add(14, 'days').endOf('day')),
    wmDateFrom: getIsoWithoutTheZ(moment.tz(moment.tz.guess()).subtract(30, 'days').startOf('day')),
    wmDateTo: getIsoWithoutTheZ(moment.tz(moment.tz.guess()).endOf('day')),
    selectedTab: '',
    evapotranspirationValueToShow: 'ETC',
    keyForRefreshing: null,
    keyForRefreshingSummaryTab: null,
    keyForRedrawing: null,
    larvalTrendShowValues: false,
    precipitationAggregation: 'HOURLY',
    soilIrrigationActivityScope: null,
    soilVisibility: {},
    showDateRangeDoesntApply: true,
    soilSettingsOpened: false,
    soilCalibrationSettingsOpened: false,
    refillPointDatesSettingsOpened: false,
    kcBlockSettingsModalOpened: false,
    fruitGrowthSettingsOpened: false,
    plantStressSettingsOpened: false,
    alternariaSettingsOpened: false,
    almondEventSettingsOpened: false,
    almondHullSplitSettingOpened: false,
    fireBlightSettingsOpened: false,
    biofixModalOpenedForInsect: null,
    flowMeterLimitsSettingsOpened: false,
    isPanelOpen: false,
    draggedPanelSize: null,
  },
  keysToPutInURL: {
    allSoilDepthsAreSelected: true,
    showSelectedSoilDepths: true,
    chillMarVsMay: true,
    chillSepVsNov: true,
    compareSeasonsInterval: true,
    dateFrom: true,
    dateTo: true,
    wmDateFrom: true,
    wmDateTo: true,
    selectedTab: true,
    evapotranspirationValueToShow: true,
    keyForRefreshing: false,
    keyForRefreshingSummaryTab: false,
    keyForRedrawing: false,
    larvalTrendShowValues: false,
    precipitationAggregation: true,
    soilIrrigationActivityScope: true,
    soilVisibility: true,
    showDateRangeDoesntApply: false,
    soilSettingsOpened: false,
    soilCalibrationSettingsOpened: false,
    refillPointDatesSettingsOpened: false,
    kcBlockSettingsModalOpened: false,
    fruitGrowthSettingsOpened: false,
    plantStressSettingsOpened: false,
    alternariaSettingsOpened: false,
    almondEventSettingsOpened: false,
    almondHullSplitSettingOpened: false,
    fireBlightSettingsOpened: false,
    biofixModalOpenedForInsect: false,
    flowMeterLimitsSettingsOpened: false,
    isPanelOpen: false,
    draggedPanelSize: false,
  },
  keysToPutInPersistentStorage: {
    allSoilDepthsAreSelected: true,
    showSelectedSoilDepths: true,
    chillMarVsMay: true,
    chillSepVsNov: true,
    compareSeasonsInterval: true,
    dateFrom: true,
    dateTo: true,
    wmDateFrom: true,
    wmDateTo: true,
    selectedTab: true,
    evapotranspirationValueToShow: true,
    keyForRefreshing: false,
    keyForRefreshingSummaryTab: false,
    keyForRedrawing: false,
    larvalTrendShowValues: false,
    precipitationAggregation: true,
    soilIrrigationActivityScope: true,
    soilVisibility: true,
    showDateRangeDoesntApply: false,
    soilSettingsOpened: false,
    soilCalibrationSettingsOpened: false,
    refillPointDatesSettingsOpened: false,
    kcBlockSettingsModalOpened: false,
    fruitGrowthSettingsOpened: false,
    plantStressSettingsOpened: false,
    alternariaSettingsOpened: false,
    almondEventSettingsOpened: false,
    almondHullSplitSettingOpened: false,
    fireBlightSettingsOpened: false,
    biofixModalOpenedForInsect: false,
    flowMeterLimitsSettingsOpened: false,
    isPanelOpen: false,
    draggedPanelSize: false,
  },
  storeName: PersistentStoreName.DETAILS_PANEL_STORE,
})

export const openDetailsPanel = () => detailsPanelStore.setState((s) => ({ ...s, isPanelOpen: true }))

export const closeDetailsPanel = () =>
  detailsPanelStore.setState((s) => ({ ...s, isPanelOpen: false, draggedPanelSize: null }))

layersNavigationMenuStore.subscribeToChanges(
  (s) => s.menuCollapsed,
  (menuCollapsed) => {
    if (!menuCollapsed && !getScreenSize().isWideScreen) closeDetailsPanel()
  },
)

smallStore.subscribeToChanges(
  (s) => s.showRegionalData,
  (showRegionalData) => {
    if (showRegionalData) {
      detailsPanelStore.setState((s) => ({
        ...s,
        compareSeasonsInterval: 0,
        keyForRefreshing: new Date().toISOString(),
      }))
    } else {
      detailsPanelStore.setState((s) => ({
        ...s,
        keyForRedrawing: new Date().toISOString(),
      }))
    }
  },
)

userDetailsStore.subscribeToChanges(
  (s) => s.isFreeRegionalUser,
  (isFreeRegionalUser) => {
    if (isFreeRegionalUser) {
      detailsPanelStore.setState((s) => ({
        ...s,
        dateFrom: getIsoWithoutTheZ(moment.tz(moment.tz.guess()).subtract(3, 'days').startOf('day')),
        dateTo: getIsoWithoutTheZ(
          moment
            .tz(moment.tz.guess())
            .add(HISTORICAL_NUMBER_OF_DAYS_FOR_REGIONAL_ONLY_USERS, 'days')
            .endOf('day'),
        ),
      }))
    } else {
      detailsPanelStore.setState((s) => ({
        ...s,
        dateFrom: getIsoWithoutTheZ(moment.tz(moment.tz.guess()).subtract(3, 'days').startOf('day')),
        dateTo: getIsoWithoutTheZ(moment.tz(moment.tz.guess()).add(14, 'days').endOf('day')),
      }))
    }
  },
)
